import { z } from 'zod'
import {
  addressSchema,
  dateSchema,
  phoneNumberSchema,
  socialSecurityNumberSchema,
  stringifiedNumberSchema
} from 'utils/custom-schemas'

const individualSchema = z.object({
  firstName: z.string().min(1),
  middleName: z.string().nullish(),
  lastName: z.string().min(1),
  dateOfBirth: dateSchema(z.date()),
  nationalities: z.array(z.string()).min(1),
  ssn: z.string().nullish(),
  identificationDocumentType: z.string().nullish(),
  idNumber: z.string().nullish(),
  residentialAddress: addressSchema,
  phoneNumber: phoneNumberSchema({ message: 'invalidPhoneNumber' }),
  email: z.string().min(1).email()
})

const beneficialOwnerBaseSchema = z.object({
  individual: individualSchema,
  relation: z.object({
    title: z.string().min(1),
    percentageOfShares: stringifiedNumberSchema(
      z.number().min(0, 'minOwnership').max(100, 'maxOwnershipTotal')
    ),
    signsTaxDocuments: z.boolean().nullish(),
    isContact: z.boolean().nullish()
  })
})

const beneficialOwnerCrossFieldRefinements = (
  data: unknown,
  ctx: z.RefinementCtx
): void => {
  const root = (data ?? {}) as {
    individual?: {
      nationalities?: string[] | null
      ssn?: string | null
      identificationDocumentType?: string | null
      idNumber?: string | null
    }
  }
  const isUs = root.individual?.nationalities?.includes('US')

  if (isUs) {
    const result = socialSecurityNumberSchema().safeParse(
      root.individual?.ssn?.trim() ?? ''
    )
    if (!result.success) {
      result.error.issues.forEach(issue =>
        ctx.addIssue({ ...issue, path: ['individual', 'ssn'] })
      )
    }
  } else {
    if (!root.individual?.identificationDocumentType?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['individual', 'identificationDocumentType']
      })
    }
    if (!root.individual?.idNumber?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['individual', 'idNumber']
      })
    }
  }
}

export const beneficialOwnerSchema = z.intersection(
  z.any().superRefine(beneficialOwnerCrossFieldRefinements),
  beneficialOwnerBaseSchema
)

export type BeneficialOwner = z.infer<typeof beneficialOwnerBaseSchema>

const individualRelationSchema = z.object({
  isUbo: z.boolean().nullish(),
  percentageOfShares: z.union([z.string(), z.number()]).nullish()
})

const individualWithRelationsSchema = z
  .object({
    parentRelations: z.array(individualRelationSchema).nullish()
  })
  .passthrough()

export const beneficialOwnerFormSchema = z.object({
  individuals: z.array(individualWithRelationsSchema).refine(
    individuals => {
      const total = individuals.reduce((sum, ind) => {
        const uboRelation = ind.parentRelations?.find(rel => rel.isUbo)
        return sum + Number(uboRelation?.percentageOfShares ?? 0)
      }, 0)
      return total <= 100
    },
    { message: 'maxOwnershipTotal' }
  )
})

export type BeneficialOwnerForm = z.infer<typeof beneficialOwnerFormSchema>
